import React from 'react';
import {observer, inject} from 'mobx-react';
import {observable, action, makeObservable} from 'mobx';
import {PropTypes} from 'prop-types';

import {utilsService} from '../core/services/utils.service';import {withRouter} from '../core/withRouter';import {AppDetailBanner} from '../components/app-detail/app-detail-banner.jsx';
import {AppVersionNavigator} from '../components/app-edit/app-version-navigator.jsx';
import {AppDetailReviews} from '../components/app-detail/app-detail-reviews.jsx';
import AppDetailInfoHeader from '../components/app-detail/app-detail-info-header.jsx';
import AppEditNavigator from '../components/app-edit/app-edit-navigator.jsx';
import AppEditableInfo from '../components/app-edit/app-editable-info.jsx';
import {AppDetailLoading} from '../components/app-detail/app-detail-loading.jsx';
import {LoadingIndicator} from '../components/loading-indicator/loading-indicator.jsx';
import {AppModal} from '../components/app-modal/app-modal';
import {NewAppValidations} from '../components/new-application/new-app-validations.jsx';
import {NewAppSubmitInterstitial} from '../components/new-application/new-app-submit-interstitial.jsx';
import {NewAppUnsavedChangesModal} from '../components/new-application/new-app-unsaved-changes-modal.jsx';
import {ButterBar} from '../components/alert-message/butter-bar';
import {StickyWrapper} from '../components/sticky-wrapper/sticky-wrapper';
import ActionButtonRenderer from '../components/action-button/action-button-renderer';
import AlertMessage from '../components/alert-message/alert-message.jsx';

@inject('store')
@observer
export class AppEditPage extends React.Component {

  static propTypes = {
    store: PropTypes.object.isRequired,
    match: PropTypes.object
  }

  @observable isLoading = false; // Must be false to trigger initial app fetch
  @observable navigatorStickyEnd = 0;
  @observable actionsCurrentlyLoading = {
    copy: false,
    remove: false,
    withdraw: false
  }

  @action setStickinessEnd() {
    this.navigatorStickyEnd = (document.querySelector('.form-actions').getBoundingClientRect().top + window.pageYOffset) - document.querySelector('.app-edit-navigator').offsetHeight;
  }

  get appId() {
    return this.props.match.params.appId
  }

  get saveId() {
    return this.props.match.params.saveId
  }

  get appVersion() {
    return utilsService.versionNumberNormalizer(this.props.match.params.appVersion)
  }

  get mode() {
    return this.props.match.params.mode
  }

  constructor(props) {
    super(props);
    makeObservable(this);
    this.masterStore = this.props.store;
    this.appDetailStore = this.props.store.appDetailStore;
    this.modalsStore = this.props.store.modalsStore;

    this.newAppMasterStore = this.props.store.newAppMasterStore;
    this.validateAppUpdate = this.newAppMasterStore.validateAppUpdate.bind(this.newAppMasterStore);
    this.submitAppUpdate = this.newAppMasterStore.submitAppUpdate.bind(this.newAppMasterStore);
    this.discardChangesAndLeave = this.newAppMasterStore.discardChangesAndLeave.bind(this.newAppMasterStore);
    this.stayAndKeepEditing = this.newAppMasterStore.stayAndKeepEditing.bind(this.newAppMasterStore);

    this.newAppDetailsStore = this.newAppMasterStore.newAppDetailsStore;
    this.newAppValidationsStore = this.newAppMasterStore.newAppValidationsStore;
    this.newAppDetailsStore.getCategoriesAndSegments();
    this.newAppDetailsStore.getAppCapabilities();


    this.newAppMasterStore.enableSaveDialogs();
    this.saveUrlPropsAndFetchApp(this.props);

    this.srAppDescription = '';
    if (this.newAppMasterStore.newAppData.appName) {
      this.srAppDescription += `app ${this.newAppMasterStore.newAppData.appName}`
    }
    if (this.newAppMasterStore.newAppData.version) {
      this.srAppDescription += ` version ${this.newAppMasterStore.newAppData.version}`
    }
  }

  componentDidUpdate(newProps) {
    this.saveUrlPropsAndFetchApp(newProps);
    if (document.querySelector('.app-edit-navigator') !== null && this.navigatorStickyEnd === 0) {
      this.setStickinessEnd();
      window.addEventListener('resize', this.setStickinessEnd);
    }
  }

  saveUrlPropsAndFetchApp = (props) => {
    /* If the user comes from within the app, we already have the app details.
		But a user could deep link into the edit page. Or if the user is coming
		from a new app page, the data may be in the wrong (apperian) format.
		In those cases, we will re-fetch the data from the server
		and store it in partial save format. Note that we don't need to re-fetch
		if only the version number has changed. */
    if (!this.isLoading &&
			props.match.params.appId !== this.newAppMasterStore.newAppData.applicationId &&
			props.match.params.saveId !== this.newAppMasterStore.newAppData.applicationId) {

      this.isLoading = true;
      const nextAppId = props.match.params.appId;
      const nextAppVersion = utilsService.versionNumberNormalizer(props.match.params.appVersion);
      this.appDetailStore.getAppVersions(nextAppId, nextAppVersion).then(() => {
        this.props.store.selectAppVersion({appId: this.appId, versionNumber: this.appVersion, saveId: this.saveId});
        this.isLoading = false;
      });
    }
  }

  componentWillUnmount() {
    this.newAppMasterStore.resetSubmissionProcess();
    this.newAppMasterStore.disableSaveDialogs();
    window.removeEventListener('resize', this.setStickinessEnd);
  }

  handleRatingsClick = () => {
    this.appDetailStore.reviewsVisible = true;
  };

  handleCopyApp = () => {
    this.actionsCurrentlyLoading.copy = true;
    return this.appDetailStore.copyApp().then(() => this.actionsCurrentlyLoading.copy = false);
  };

  handleRemoveApp = (reason) => {
    this.actionsCurrentlyLoading.remove = true;
    return this.appDetailStore
		.removeApp(reason)
		.then(() => this.actionsCurrentlyLoading.remove = false);
  };

  handleWithdrawApp = (reason) => {
    this.actionsCurrentlyLoading.withdraw = true;
    return this.appDetailStore
		.withdrawApp(reason)
		.then(() => this.actionsCurrentlyLoading.withdraw = false);
  };

  handleToggleModal = (modalName) => {
    this.modalsStore.toggleModal(modalName);
  }

  renderAppDetailInfoFooter() {
    const status = this.newAppMasterStore.newAppData.status && this.mode === 'view'
      ? this.newAppMasterStore.newAppData.status
      : '';
    const buttonsForPageMode = {
      view: [
        {
          label: 'Edit',
          className: 'fn-primary',
          linkTo: utilsService.generateAppPageURI(this.newAppMasterStore.newAppData, 'edit'),
          srText: 'App Details'
        }, {
          label: 'Copy',
          className: 'fn-secondary',
          isLoading: this.actionsCurrentlyLoading.copy,
          loadingLabel: 'Copy',
          onClick: this.handleCopyApp,
          srText: this.srAppDescription
        }, {
          label: 'Remove',
          className: 'as-link',
          isLoading: this.actionsCurrentlyLoading.remove,
          onClick: () => this.handleToggleModal('removeApp'),
          srText: this.srAppDescription
        }, {
          label: 'Withdraw',
          className: 'as-link',
          isLoading: this.actionsCurrentlyLoading.withdraw,
          onClick: () => this.handleToggleModal('withdrawApp'),
          srText: this.srAppDescription
        }
      ],
      review: [
        {
          label: 'Push Changes',
          className: 'fn-primary',
          onClick: this.submitAppUpdate
        }, {
          label: 'Back To Edit',
          linkTo: utilsService.generateAppPageURI(this.newAppMasterStore.newAppData, 'edit'),
          className: 'as-btn fn-secondary'
        }
      ],
      edit: [
        {
          label: 'Review Changes',
          linkTo: utilsService.generateAppPageURI(this.newAppMasterStore.newAppData, 'review'),
          className: 'as-btn fn-primary',
          onClick: this.validateAppUpdate,
          disabled: !this.newAppMasterStore.newAppFormIsDirty
        }, {
          label: 'Discard & Go Back',
          linkTo: utilsService.generateAppPageURI(this.newAppMasterStore.newAppData, 'view'),
          className: 'as-btn fn-secondary'
        }
      ]
    }
    return (<ActionButtonRenderer className="app-detail-footer-buttons" status={status}>
      {buttonsForPageMode[this.mode]}
    </ActionButtonRenderer>)
  }

  renderSideBar() {
    if (this.mode === 'view') {
      return (<AppVersionNavigator appVersions={this.appDetailStore.appVersions} name={this.appDetailStore.appVersions.app_name} handleRatingsClick={this.handleRatingsClick} handleVersionSelect={this.props.store.selectAppVersion.bind(this.props.store)} reviewsVisible={this.appDetailStore.reviewsVisible} selectedVersion={this.appDetailStore.selectedVersion}/>)
    } else if (this.mode === 'edit') {
      return (<StickyWrapper stickyEnd={this.navigatorStickyEnd}>
        <AppEditNavigator name={this.newAppMasterStore.newAppData.appName} status={this.newAppMasterStore.newAppData.status}/>
      </StickyWrapper>)
    } else {
      return null;
    }
  }

  renderMainContent() {
    let mainContent = null;
    if (this.appDetailStore.reviewsVisible) {
      mainContent = (<AppDetailReviews reviews={this.appDetailStore.appReviews} rating={this.appDetailStore.appVersions.rating || 0} reviewsTotal={this.appDetailStore.appVersions.reviews.length}/>)
    } else {
      mainContent = (<AppEditableInfo masterStore={this.masterStore} readOnly={this.mode !== 'edit'}/>)
    }
    return (<React.Fragment>
      <div className="app-info-head">
        <AppDetailInfoHeader app={this.newAppMasterStore.newAppData} pageMode={this.mode} isDirty={this.newAppMasterStore.newAppFormIsDirty} validateAppUpdate={this.validateAppUpdate} pushChanges={this.submitAppUpdate} actionsCurrentlyLoading={this.actionsCurrentlyLoading} handleToggleModal={this.handleToggleModal} handleCopyApp={this.handleCopyApp} showActionButtons={!this.appDetailStore.reviewsVisible}/>
      </div>
      <div>
        {mainContent}
      </div>
    </React.Fragment>)
  }

  renderAlertBars = () => {
    return (<React.Fragment>
      {
        this.newAppMasterStore.appSuccessfullyEdited === true && <div className="container">
            <ButterBar autoFocus type="success" message="<strong>Success!</strong> Your app was updated." handleCloseClick={() => this.newAppMasterStore.appSuccessfullyEdited = null}/>
          </div>
      }
      {
        this.newAppMasterStore.appSuccessfullyEdited === false && <div className="container">
            <ButterBar autoFocus type="error" message="<strong>Error:</strong> An unknown error occured. Please try again or contact us for support." handleCloseClick={() => this.newAppMasterStore.appSuccessfullyEdited = null}/>
          </div>
      }
    </React.Fragment>)
  }

  renderLoadingView() {
    return (<div>
      <AppDetailLoading/>
      <LoadingIndicator isLoading={!this.appDetailStore.appDataIsAvailable}/>
    </div>);
  }

  withdrawAppModalBody = () => {
    return (<div>
      <p>This app will not be reviewed by the FirstNet team.</p>
      <p>Previously approved versions of the app will not be affected.</p>
    </div>)
  };

	renderRemoveAppModal = () => {
    return (
			<AppModal
				type="removeApp"
				name={this.newAppMasterStore.newAppData.appName}
				platform={this.newAppMasterStore.newAppData.platform}
				version={this.newAppMasterStore.newAppData.version}
				icon={this.newAppMasterStore.newAppData.iconImageURLAndDescription}
				title="Remove From App Catalog?"
				body={<p> No one will be able to download this app from the catalog, but current users will still be able to use the application.</p>}
				cancelButtonText="Keep App"
				confirmationButtonText="Remove App"
				confirmationActionButtonText="Removing"
				disableConfirmationButton={this.actionsCurrentlyLoading.remove}
				handleToggleModal={this.handleToggleModal}
				handleConfirmationClick={this.handleRemoveApp}
				modalIsVisible={this.modalsStore.modalVisibilities.removeApp} />
		)
  };

  renderWithdrawAppModal = () => {
    return (
			<AppModal
				type="withdrawApp"
				name={this.newAppMasterStore.newAppData.appName}
				platform={this.newAppMasterStore.newAppData.platform}
				version={this.newAppMasterStore.newAppData.version}
				icon={this.newAppMasterStore.newAppData.iconImageURLAndDescription}
				title="Withdraw Submission?"
				body={this.withdrawAppModalBody()}
				cancelButtonText="Keep Submission"
				confirmationButtonText="Withdraw Submission"
				confirmationActionButtonText="Withdrawing"
				disableConfirmationButton={this.actionsCurrentlyLoading.withdraw}
				handleToggleModal={this.handleToggleModal}
				handleConfirmationClick={this.handleWithdrawApp}
				modalIsVisible={this.modalsStore.modalVisibilities.withdrawApp} />
		)
  };

  renderNewAppUnsavedChangesModal = () => {
    return (
			<NewAppUnsavedChangesModal
				discardChangesAndLeave={this.discardChangesAndLeave}
				stayAndKeepEditing={this.stayAndKeepEditing}
				modalVisibilities={this.modalsStore.modalVisibilities}
				saveChangesAndLeaveEnabled={false} />
		)
  }

  render() {
    if (this.isLoading) {
      return this.renderLoadingView()
    }
    return (<div className={`app-edit-page page-mode-${this.mode}`}>
      {(this.newAppMasterStore.showLoadingOverlay && <NewAppSubmitInterstitial store={this.newAppMasterStore} actionText="Pushing Changes" buttonText="Cancel Push"/>)}
      <AppDetailBanner data={this.appDetailStore.appBannerProps}/>
      <NewAppValidations reRouteOnErrorClick={false} store={this.newAppValidationsStore}/> {this.renderAlertBars()}
      <div className="container">
        <AlertMessage/>
        <div className="row">
          <div className={`edit-sidebar ${this.mode === 'review'
              ? 'hidden'
              : 'col-3'}`}>
            {this.renderSideBar()}
          </div>
          <div className={`edit-form col-${this.mode === 'review'
              ? '12'
              : '9'}`}>
            {this.renderMainContent()}
          </div>
        </div>
        {!this.appDetailStore.reviewsVisible &&
          <div className="row">
            <div className={`form-actions col-${this.mode === 'review'
                ? '12'
                : '9'} offset-${this.mode === 'review'
                  ? '0'
                  : '3'}`}>
              {this.renderAppDetailInfoFooter()}
            </div>
          </div>
        }
      </div>
      {this.modalsStore.modalVisibilities.unsavedChanges && this.renderNewAppUnsavedChangesModal()}
      {this.modalsStore.modalVisibilities.removeApp && this.renderRemoveAppModal()}
      {this.modalsStore.modalVisibilities.withdrawApp && this.renderWithdrawAppModal()}
    </div>)
  }
}

export const AppEditPageWithRouter = withRouter(AppEditPage);
